// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * A module's 「安装履历」 (component design §9.4): every robot or module it has been fitted
 * in, which slot, and from when to when — derived by the server from the versions that
 * pinned it, not kept on the side.
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "@/api/endpoints";
import { ErrorBanner } from "@/components/ErrorBanner";
import { useArchive } from "./ArchiveDetailPage";

export function ArchiveInstallations() {
  const { t } = useTranslation();
  const { archive } = useArchive();
  const installations = useQuery({
    queryKey: ["archive-installations", archive.id],
    queryFn: () => api.archives.installations(archive.id),
  });

  if (installations.error) return <ErrorBanner error={installations.error} />;
  if (!installations.data) return <p className="muted">{t("common.loading")}</p>;
  if (installations.data.length === 0) {
    return <p className="muted">{t("moduleArchive.neverInstalled")}</p>;
  }
  return (
    <table>
      <thead>
        <tr>
          <th>{t("moduleArchive.parent")}</th>
          <th>{t("archive.position")}</th>
          <th>{t("moduleArchive.installedAt")}</th>
          <th>{t("moduleArchive.removedAt")}</th>
          <th>{t("moduleArchive.parentVersions")}</th>
        </tr>
      </thead>
      <tbody>
        {installations.data.map((stay) => (
          <tr key={`${stay.parent_archive_id}-${stay.position_code}-${stay.installed_at}`}>
            <td>
              <Link to={`/archives/${stay.parent_archive_id}`}>{stay.parent_archive_no}</Link>{" "}
              <span className="muted">
                {stay.parent_kind === "MODULE" ? t("archive.kindModule") : t("archive.kindRobot")}
              </span>
            </td>
            <td>{stay.position_name}</td>
            <td>{new Date(stay.installed_at).toLocaleString()}</td>
            <td>
              {stay.removed_at ? (
                new Date(stay.removed_at).toLocaleString()
              ) : (
                <span className="badge">{t("moduleArchive.stillInstalled")}</span>
              )}
            </td>
            <td>
              {stay.first_version === stay.last_version
                ? `v${stay.first_version}`
                : `v${stay.first_version} – v${stay.last_version}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
